/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import * as Sentry from '@sentry/react';
import { DegreePlan_LearnerAdmissionStatus as DegreePlanLearnerAdmissionStatus } from '__generated__/graphql-types';

import { useRetracked } from 'js/lib/retracked';

import { Button, Hidden, Typography, breakpoints } from '@coursera/cds-core';

import EstimatedGraduationContextualHelp from 'bundles/degree-home/components/v3-dpm/components/EstimatedGraduationDate/EstimatedGraduationContextualHelp';
import EstimatedGraduationDescription from 'bundles/degree-home/components/v3-dpm/components/EstimatedGraduationDate/EstimatedGraduationDescription';
import TermBadge from 'bundles/degree-home/components/v3-dpm/components/EstimatedGraduationDate/TermBadge';
// TODO get rid of circular dependency: https://coursera.atlassian.net/browse/CPM-2491
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import PlanningToolDialog from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/PlanningToolDialog';
import { DPMPT_SENTRY_PRODUCT_TAG, PATHS } from 'bundles/degree-home/components/v3-dpm/constants/constants';
import { VIEW_PLAN_BUTTON } from 'bundles/degree-home/components/v3-dpm/constants/trackingNameConstants';
import type { UseCurriculum_EstimatedAcademicTermFragment as EstimatedAcademicTerm } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/dpmSharedFragments';
import type {
  UseCurriculum_DegreeCurriculumFragment as DegreeCurriculumFragment,
  UseCurriculum_PbaCurriculumFragment as PbaCurriculumFragment,
} from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/useCurriculum';
import { verifyDegreeCompletionStatus } from 'bundles/degree-home/components/v3-dpm/lib/gradeUtils';
import { getEstimatedGraduationTermSummary } from 'bundles/degree-home/components/v3-dpm/lib/graduationUtils';
import { isDegreeCurriculum } from 'bundles/degree-home/components/v3-dpm/lib/otherUtils';
import useCurriculum from 'bundles/degree-home/components/v3-dpm/lib/useCurriculum';
import useDegreePlan from 'bundles/degree-home/components/v3-dpm/lib/useDegreePlan';
import useProgressionCalculator from 'bundles/degree-home/components/v3-dpm/lib/useProgressionCalculator';
import type { EstimatedGradTermSummary } from 'bundles/degree-home/components/v3-dpm/types/graduationTypes';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import type { TrackingProps } from 'bundles/page/components/TrackedLink2';
import { Card } from 'bundles/program-loggedin-shared/components/Card';

import BackgroundImage1 from 'images/degree-home/BackgroundImage1.png';

import _t from 'i18n!nls/degree-home';

export type GraduationCardVariant = 'summary' | 'detail';

const styles = {
  cardContainer: (variant: GraduationCardVariant) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    margin-bottom: ${variant === 'detail' ? 'var(--cds-spacing-400)' : 0};

    ${breakpoints.up('sm')} {
      background-image: url(${BackgroundImage1});
      background-repeat: no-repeat;
      background-size: contain;
      background-position: 100%;
    }

    ${breakpoints.down('md')} {
      /* This forces the background image to not collide with the left text */
      background-position: max(250px, 100%) 50%;
    }
  `,

  innerContainer: css`
    display: flex;
    flex: 1;
    flex-direction: column;

    ${breakpoints.only('xs')} {
      flex-basis: 100%;
    }
  `,

  termBadgeContainer: css`
    flex: 1;
    display: flex;
    justify-content: center;
  `,

  termBadgeMobileContainer: css`
    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-200);
      margin-bottom: var(--cds-spacing-200);
    }
  `,
  description: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
  `,
  viewPlanButton: css`
    margin-top: var(--cds-spacing-200);
  `,
};

type WrapperProps = {
  variant: GraduationCardVariant;
};
type CardProps = {
  variant: GraduationCardVariant;
  hasCompletedDegree: boolean;
  termNum: number;
  creditNumPerTerm: number | null;
  termName: string | null;
};

export const EstimatedGraduationCard = ({
  variant,
  hasCompletedDegree,
  termNum,
  creditNumPerTerm,
  termName,
}: CardProps) => {
  const [isPlanOpen, setPlanModalOpen] = React.useState(false);
  const track = useRetracked();

  const handleOpenDialog = (trackingName: string, openDialogFunction: () => void) => {
    track({
      action: 'click',
      trackingName,
      trackingData: {},
    });
    openDialogFunction();
  };
  return (
    <React.Fragment>
      <Card css={styles.cardContainer(variant)}>
        <div css={styles.innerContainer}>
          <div css={styles.description}>
            {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
            <Typography variant="h2semibold">{_t('Estimated graduation date')}</Typography>
            <Hidden smUp>
              <div css={styles.termBadgeMobileContainer}>
                {!hasCompletedDegree && <TermBadge termName={termName} />}
              </div>
              {/* TODO here: display the actual graduation term when the learner has completed the degree: https://coursera.atlassian.net/browse/CPM-2478  */}
            </Hidden>
            <EstimatedGraduationDescription
              hasCompletedDegree={hasCompletedDegree}
              termNum={termNum}
              creditNumPerTerm={creditNumPerTerm}
            />
            <EstimatedGraduationContextualHelp />
          </div>
          <div>
            {variant === 'detail' && (
              <div css={styles.viewPlanButton}>
                <Hidden smDown>
                  <Button
                    trackingName={VIEW_PLAN_BUTTON}
                    withVisibilityTracking
                    component={(props: TrackingProps) => <TrackedLink2 {...props} href={`./${PATHS.ACADEMICS}`} />}
                    size="small"
                    variant="secondary"
                    aria-label={_t('View plan')}
                  >
                    {_t('View plan')}
                  </Button>
                </Hidden>
                <Hidden mdUp>
                  <Button
                    variant="secondary"
                    size="small"
                    aria-label={_t('View plan')}
                    css={styles.viewPlanButton}
                    onClick={() => handleOpenDialog(VIEW_PLAN_BUTTON, () => setPlanModalOpen(true))}
                  >
                    {_t('View plan')}
                  </Button>
                </Hidden>
              </div>
            )}
          </div>
        </div>
        {!hasCompletedDegree && (
          <div css={styles.termBadgeContainer}>
            <Hidden xsDown>
              <TermBadge termName={termName} />
            </Hidden>
          </div>
        )}
      </Card>
      <PlanningToolDialog open={isPlanOpen} closeModal={() => setPlanModalOpen(false)} />
    </React.Fragment>
  );
};

export const EstimatedGraduationCardWrapper = ({ variant }: WrapperProps) => {
  const curriculumGql = useCurriculum();
  const curriculum = curriculumGql.data?.Degree?.queryById?.curriculum;
  const curriculumsData: (DegreeCurriculumFragment | PbaCurriculumFragment)[] = curriculum?.curriculums || [];
  const isLearnerNotEnrolled =
    curriculumGql?.data?.Degree?.queryById?.curriculum?.learnerAdmissionStatus ===
    DegreePlanLearnerAdmissionStatus.NotEnrolled;
  const degreeCurriculum = curriculumsData.find((curr) => isDegreeCurriculum(curr)) as
    DegreeCurriculumFragment | undefined;
  const overallProgression = useProgressionCalculator();

  const degreePlanGql = useDegreePlan();
  const termPlans = degreePlanGql.data?.LearnerPlan?.queryPlan?.learnerPlan;

  if (!degreeCurriculum || !overallProgression || !termPlans || isLearnerNotEnrolled) {
    return null;
  }
  const terms: EstimatedAcademicTerm[] = curriculumGql?.data?.Degree?.queryById?.academicTermsPaginated?.elements ?? [];

  const hasCompletedDegree = verifyDegreeCompletionStatus(degreeCurriculum, overallProgression);

  let estimatedGraduationDateData: EstimatedGradTermSummary | null = null;

  try {
    estimatedGraduationDateData = getEstimatedGraduationTermSummary(
      degreeCurriculum,
      overallProgression,
      termPlans,
      terms
    );
  } catch (e) {
    Sentry.captureException(e, { tags: DPMPT_SENTRY_PRODUCT_TAG });
    return null;
  }

  if (!estimatedGraduationDateData) {
    return null;
  }

  const { termNum, creditNumPerTerm, termName } = estimatedGraduationDateData;

  return (
    <EstimatedGraduationCard
      variant={variant}
      hasCompletedDegree={hasCompletedDegree}
      termNum={termNum}
      creditNumPerTerm={creditNumPerTerm}
      termName={termName}
    />
  );
};

export default EstimatedGraduationCardWrapper;
